import { useState, useRef } from 'react'
import { X, Plus, Loader2, Mail } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'

interface RecipientsWidgetProps {
  initialRecipients: string[]
  onSave: (recipients: string[]) => Promise<void>
}

export function RecipientsWidget({ initialRecipients, onSave }: RecipientsWidgetProps) {
  const [recipients, setRecipients] = useState<string[]>(initialRecipients)
  const [inputValue, setInputValue] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const addRecipient = () => {
    const value = inputValue.trim().toLowerCase()
    if (!value) return

    if (recipients.includes(value)) {
      toast.error(`"${value}" is already in the list`)
      return
    }

    setRecipients((prev) => [...prev, value])
    setInputValue('')
    setIsDirty(true)
    inputRef.current?.focus()
  }

  const removeRecipient = (recipient: string) => {
    setRecipients((prev) => prev.filter((r) => r !== recipient))
    setIsDirty(true)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addRecipient()
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await onSave(recipients)
      setIsDirty(false)
      toast.success('Recipients saved')
    } catch {
      toast.error('Failed to save recipients')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDiscard = () => {
    setRecipients(initialRecipients)
    setInputValue('')
    setIsDirty(false)
  }

  return (
    <Card>
      <CardHeader className="border-b">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Recipients
            </CardTitle>
            <CardDescription className="mt-1">
              Email address prefixes accepted by this domain (e.g. "news" → news@yourdomain.com)
            </CardDescription>
          </div>
          {isDirty && (
            <span className="text-xs text-amber-500 font-medium">Unsaved changes</span>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4 pt-4">
        {/* Tag list */}
        <div className="flex flex-wrap gap-2 min-h-10">
          {recipients.length === 0 ? (
            <p className="text-sm text-muted-foreground">No recipients added yet.</p>
          ) : (
            recipients.map((recipient) => (
              <span
                key={recipient}
                className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2.5 py-1 text-sm font-medium text-primary ring-1 ring-primary/20"
              >
                {recipient}
                <button
                  type="button"
                  onClick={() => removeRecipient(recipient)}
                  className="ml-0.5 rounded hover:bg-primary/20 p-0.5 transition-colors"
                  aria-label={`Remove ${recipient}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))
          )}
        </div>

        {/* Add input */}
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add recipient (e.g. news)"
            className="max-w-xs"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={addRecipient}
            disabled={!inputValue.trim()}
            aria-label="Add recipient"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2 border-t">
          <Button onClick={handleSave} disabled={!isDirty || isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
          {isDirty && (
            <Button variant="outline" onClick={handleDiscard} disabled={isSaving}>
              Discard
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
